//
// TLS Dose Calculation Service Dose Scaling Service (DSS) Page.
//
import { useState, useEffect } from 'react'

import gUtilities from '../script/utilities';
import gSession from '../script/session';

import EditForm from './EditForm';
import CardWidget from './CardWidget';
import TableWidget from './TableWidget';
import TextWidget from './TextWidget';
import ButtonWidget from './ButtonWidget';
import SelectWidget from './SelectWidget';
import PlotWidget from './PlotWidget';

const k_dss_report_titles = ["Name", "Value"];
const k_dss_report_columns = ["name", "value"];
const k_pk_modes = ["Continuous", "Decaying", "Linear"];

const DssPage = () => {
    const [bbc, setBbc] = useState(0.0);
    const [config, setConfig] = useState({});
    const [scaled, setScaled] = useState(false);
    const [status, setStatus] = useState("Press Refresh Button");
    const [report, setReport] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [pk_mode, setPkMode] = useState("Continuous");
    const [mounted, setMounted] = useState(false);
    const [mu_per_sec, setMuPerSec] = useState(0.0);
    const [parameter1, setParameter1] = useState(0.0);
    const [edit_config, setEditConfig] = useState(false);
    const [prescription, setPrescription] = useState(null);
    const [prescriptions, setPrescriptions] = useState([]);
    const [prescription_ids, setPrescriptionsIds] = useState([]);
    const [tmt_time_offset_ms, setTmtTimeOffset] = useState("0 s");
    const [time_between_beams_ms, setTimeBetweenBeams] = useState("0 s");
    
    function buildRequest(name, data={}) {
        let request = {
            command: "dss.request",
            dss_request_name: name,
            "request": data
        };

        return request;
    }

    async function dss(name, data={}) {
        if (!isMounted()) {
            return { isError: true, message: "Page is not mounted"};
        }

        let request = buildRequest(name, data);

        console.log("REQUEST", request);

        let response = await gSession.exchange(request);

        if (response.hasOwnProperty("code") && !response.code.endsWith("Success")) {
            gUtilities.showError(name, `(${response.code}) ${response.message}`);
            response = { isError: true, message: response.message, code: response.code };
        }

        console.log("RESPONSE", response);

        return response;
    }

    async function db(collection, query, many=false) {
        let request = {
            service: "db.query",
            collection: collection,
            many: many,
            query: query
        };

        let response = await gSession.exchange(request);

        return response;
    }

    async function onDebug() {
        let request = {
            level: "DebugLevelHigh"
        };

        let response = await dss("DebugRequest", request);

        if (response.isError) {
            gUtilities.showError("Debug", response.message);
        }
    }
    
    function onPrescription(index) {
        let item = prescriptions[index];

        setPrescription(item);
        setLoaded(false);
        setScaled(false);
        setReport(null);
    }

    async function onRefresh() {
        let query = {
            "status.state": "ACCEPTED",
            "jversion": "1.1"
        };

        let response = await db("dcs_prescription", query, true);

        if (response.isError) {
            gUtilities.showError("Refresh", response.message);
        } else {
            setPrescriptions(response.list);

            let ids = [];
            response.list.map((prescription) => {
                ids.push(`${prescription.prescription_description} (${prescription.request.patient_id}, ${prescription.plan_name})`);
            });

            setPrescriptionsIds(ids);
        }
    }

    async function onPing() {
        let response = await dss("PingRequest");

        if (response.isError) {
            gUtilities.showError("Ping DSS", response.message);
        } else {
            setStatus(`Version ${response.version}`);
        }
    }

    // detect existance of this page's root <div>
    function isMounted() {
        return gUtilities.isMounted("w_DssPageRootDiv");
    }

    function getMedicalID() {
        if (prescription && prescription.request.patient_id) {
            return prescription.request.patient_id;
        }
        
        return "N/A";
    }

    function getPlanName() {
        if (prescription && prescription.plan_name) {
            return prescription.plan_name;
        }
        
        return "N/A";
    }

    function getDescription() {
        if (prescription && prescription.prescription_description) {
            return prescription.prescription_description;
        }
        
        return "N/A";
    }

    function getTimestamp() {
        if (prescription && prescription.timestamp) {
            return prescription.timestamp;
        }
        
        return "N/A";
    }

    function getRoiName() {
        if (prescription && prescription.roi_name) {
            return prescription.roi_name;
        }
        
        return "N/A";
    }

    function getDose() {
        if (prescription && prescription.analysis && prescription.analysis.dose_dvh) {
            return prescription.analysis.dose_dvh;
        }
        
        return "N/A";
    }

    function getBeamCount() {
        if (prescription && prescription.beams.length) {
            return prescription.beams.length;
        }
        
        return "N/A";
    }

    function getVolumePercent() {
        if (prescription && prescription.analysis && prescription.analysis.volume_percent) {
            return prescription.analysis.volume_percent;
        }
        
        return "N/A";
    }

    // Checkin simulates LDS downloading the prescription data file.
    async function Checkin() {
        setStatus("Checking in...");

        let request = {
            prescription_id: prescription.prescription_id,
            key: prescription.dss_package_key
        };

        let response = await dss("treatment.checkin", request);

        if (response.isError) {
            gUtilities.showError("Load", response.message);
            setStatus("Error");
            return false;
        }

        setStatus("Checked-in");

        return true;
    }

    async function onCheckIn() {
        setReport(null);

        let checked_in = await Checkin();
        if (!checked_in) {
            setStatus("Error");
            setLoaded(false);
            return;
        }

        let request = {
            prescription_id: prescription.prescription_id,
            mode: pk_mode,
            parameters: [
                parameter1
            ]
        };

        let response = await dss("LoadRequest", request);

        if (response.isError && response.code !== "LoadCodeAlreadyLoaded") {
            gUtilities.showError("Load", response.message);
            setStatus("Error");
            setLoaded(false);
        } else {
            setStatus("Loaded");
            setLoaded(true);
        }
    }
    
    async function onScale() {
        setReport(null);

        let request = {
            prescription_id: prescription.prescription_id,
            bbc_ppm: bbc,
            measurement_time: { ms: 0 },
            treatment_time: { ms: tmt_time_offset_ms },
            time_between_beams: { ms: time_between_beams_ms },
            calibrated_mu_per_second: mu_per_sec
        };

        let response = await dss("ScalingRequest", request);
        console.log("ScalingResponse", response);

        if (response.isError) {
            gUtilities.showError("Scale", response.message);
            setStatus("Error");
            setScaled(false);
        }
        else {
            setScaled(true);
            setStatus("Scaling complete; loading report");
            await onReport();
        }
    }
    
    async function onReport() {
        let request = {
            prescription_id: prescription.prescription_id
        };

        let response = await dss("ReportRequest", request);

        if (response.isError) {
            gUtilities.showError("Report", response.message);
            setStatus("Error");
        } else {
            console.log("DEBUG: [onReport]", response.report);
            setReport(response.report);
            console.log("DEBUG: [onReport] SET");
            setStatus("Report Available");
        }
    }
    
    async function onCheckOut() {
        let request = {
            prescription_id: prescription.prescription_id
        };

        let response = await dss("RemoveRequest", request);

        if (response.isError) {
            gUtilities.showError("Remove", response.message);
            setStatus("Error");
        } else {
            setStatus("Removed");
        }
    }

    function scalingReport() {
            console.log("DEBUG: [scalingReport]", report);
        let data = [
            { name: "Prescription", value: report.prescription_name },
            { name: "Treatment BBC", value: report.tmt_bbc },
            { name: "Loops", value: report.loops },
            { name: "Scaling Time (ms)", value: report.duration },
            { name: "Timestamp", value: report.timestamp }
        ];

            console.log("DEBUG: [scalingReport] DATA");
        return data;
    }

    function beamTitle(beam) {
        let title =  `Beam (${beam.number}) ${beam.name}`;

        return title;
    }

    function beamReport(beam) {
                    console.log("DEBUG: [beamReport]", beam);

        let data = [
            { name: "Plan BBC", value: beam.plan_bbc },
            { name: "Plan MU", value: beam.plan_mu },
            { name: "Plan Dose (Gy)", value: beam.plan_dose },
            { name: "DSS BCC", value: beam.result.bbc },
            { name: "DSS MU", value: beam.result.mu },
            { name: "DSS Dose (Gy)", value: beam.dss_dose },
            { name: "TMT Start (ms)", value: beam.start},
        ];
                    console.log("DEBUG: [beamReport] DONE");

        return data;
    }

    async function onEditScalingConfiguration() {
        let query = {
            name: "dss"
        };

        let response = await db("dcs_configuration", query);
        if (response.isError) {
            gUtilities.showError("Configuration", response.message);            
        } else {
            setConfig(response.scaling);
            setEditConfig(true);
        }
    }

    async function onUpdateScalingConfiguration(edited) {
        setEditConfig(false);

        config.scaling = edited;

        // update the database
        let request = {
            command: "dss.db-update",
            update: {
                field: "scaling",
                data: edited
            },
            query: {
                collection: "dcs_configuration",
                name: "name",
                value: "dss"
            }
        };

        let response = await gSession.exchange(request);

        if (response.isError) {
            gUtilities.showError("Debug", response.message);
        } else {
            // update the DSS service 
            await sendConfigurationRequest(edited);
        }
    }

    async function sendConfigurationRequest(scaling) {
        let response = await dss("ConfigurationRequest", { scaling: scaling });

        if (response.isError) {
            gUtilities.showError("Send Configuration", response.message);
        }
    }

    function onPkMode(index) {
        let mode = k_pk_modes[index];

        setPkMode(mode);
    }

    function onParameter1(f) {
        setParameter1(f);
    }

    function onBbc(f) {
        setBbc(f);
    }

    function onTmtTimeOffset(ms) {
        setTmtTimeOffset(ms);
    }

    function onTimeBetweenBeams(ms) {
        setTimeBetweenBeams(ms);
    }

    function onMuPerSec(f) {
        setMuPerSec(f);
    }

    function Normalize(values) {
        let max = 0;
        for (let value of values) {
            if (value > max) {
                max = value;
            }
        }

        if (max === 0) {
            return values;
        }

        let normalized = [];
        for (let value of values) {
            normalized.push(100.0 * value / max);
        }

        return normalized;
    }

    function getPlot(beam) {
                    console.log("DEBUG: [getPlot]", beam);

        let curves = [
            {
                x: beam.dvh.x,
                y: beam.dvh.dvh,
                name: `DVH`,
            },
            {
                x: beam.dvh.x,
                y: Normalize(beam.dvh.histogram),
                name: `Histogram`,
            }
        ];

        let plot = {
            xlegend: "Dose (Gy)",
            ylegend: "Percent or Count",
            title: "DVH",
            curves: curves
        }

                    console.log("DEBUG: [getPlot] DONE");
        return plot;
    }

    useEffect(() => {
        if (!mounted) {
            onDebug();
            onRefresh();
            setMounted(true);
        }

        // cleanup function
        return () => {
        };
    }, [prescription, prescriptions, edit_config, scaled, loaded]);

    return (
        <div id="w_DssPageRootDiv">
            <h1><span></span></h1>
            <CardWidget title="Setup" level="secondary">
                <div className="row">
                    <div className="col-md-3">
                        <ButtonWidget width="large" title="Prescriptions" text="Refresh" onClick={onRefresh} />
                    </div>
                    <div className="col-md-3">
                        <ButtonWidget width="large" title="Configure" text="Edit" onClick={onEditScalingConfiguration} />
                    </div>
                    <div className="col-md-3">
                        <ButtonWidget width="large" title="Connectivity" text="Ping" onClick={onPing} />
                    </div>
                    <div className="col-md-3">
                        <TextWidget title="Status" text={status} />
                    </div>
                </div>
            </CardWidget>
            <CardWidget title="Prescription" level="primary">
                <div className="row">
                    <div className="col">
                        <SelectWidget title="Prescription List" items={prescription_ids} onSelect={onPrescription}/>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-3">
                            <TextWidget title="Medical ID" text={getMedicalID()} />
                    </div>
                    <div className="col-md-3">
                            <TextWidget title="Description" text={getDescription()} />
                    </div>
                    <div className="col-md-3">
                            <TextWidget title="Plan" text={getPlanName()} />
                    </div>
                    <div className="col-md-3">
                            <TextWidget title="Timestamp" text={getTimestamp()} />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-3">
                            <TextWidget title="ROI" text={getRoiName()} />
                    </div>
                    <div className="col-md-3">
                            <TextWidget title="Dose (Gy)" text={getDose()} />
                    </div>
                    <div className="col-md-3">
                            <TextWidget title="Beam Count" text={getBeamCount()} />
                    </div>
                    <div className="col-md-3">
                            <TextWidget title="Volume %" text={getVolumePercent()} />
                    </div>
                </div>
            </CardWidget>
            <CardWidget title="Patient Check In" level="secondary" open={prescription ? true : false}>
                <div className="row">
                    <div className="col-md-3">
                        <SelectWidget title="P-K Mode" items={k_pk_modes} onSelect={onPkMode}/>
                    </div>
                    <div className="col-md-3">
                        <TextWidget title="Parameter" text={parameter1} onChange={onParameter1} type="float" />
                    </div>
                    <div className="col-md-3">
                        <ButtonWidget width="large" title="Load" text="Check In" onClick={onCheckIn} />
                    </div>
                    <div className="col-md-3">
                        <ButtonWidget width="large" title="Unload" text="Check Out" onClick={onCheckOut} />
                    </div>
                </div>
            </CardWidget>
            <CardWidget title="Scaling" level="secondary" open={loaded}>
                <div className="row">
                    <div className="col-md-3">
                        <TextWidget onChange={onBbc} title="BBC (ppm)" text={bbc} type="float"></TextWidget>
                    </div>
                    <div className="col-md-3">
                        <TextWidget onChange={onTmtTimeOffset} title="Treatment Time Offset (TS)" text={tmt_time_offset_ms} type="time-span"></TextWidget>
                    </div>
                    <div className="col-md-3">
                        <TextWidget onChange={onTimeBetweenBeams} title="Time Between Beams (TS)" text={time_between_beams_ms} type="time-span"></TextWidget>
                    </div>
                    <div className="col-md-3">
                        <TextWidget onChange={onMuPerSec} title="Calibration (MU/sec)" text={mu_per_sec} type="float"></TextWidget>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <ButtonWidget title="Calculate" text="Scale" onClick={onScale}  width="1400px" />
                    </div>
                </div>
            </CardWidget>
            <CardWidget title="Report" level="light" open={report ? true : false}>
                { report &&
                    <TableWidget name="Scaling Report" data={scalingReport()} columns={k_dss_report_columns} titles={k_dss_report_titles}></TableWidget>
                }
            </CardWidget>
            { report &&
                report.beams.map((beam) => (
                    <>
                        <CardWidget title={beamTitle(beam)} level="light">
                            <TableWidget name="Beam Report" data={beamReport(beam)} columns={k_dss_report_columns} titles={k_dss_report_titles}></TableWidget>
                        </CardWidget>
                        <CardWidget title="DVH" level="primary">
                            <div className="row">
                                <div className="col-md-4">
                                    <PlotWidget title="Histogram and DVH" curves={getPlot(beam)}></PlotWidget>
                                </div>
                            </div>
                        </CardWidget>
                    </>
                ))
            }
            { edit_config &&
                <EditForm title="Scaling Configuration" data={config} onApply={onUpdateScalingConfiguration} onClose={() => setEditConfig(false)}></EditForm>
            }
        </div>
    );
}

export default DssPage;
